/**
 * Solid Food Introduction Schedule
 * India-appropriate, WHO-aligned (start at 6 months)
 * Diet flags: 'veg' = safe for vegetarians, 'egg' = includes eggs, 'nonveg' = meat/fish, 'jain' = jain safe
 */

export type DietFlag = 'veg' | 'egg' | 'nonveg' | 'jain';
export type FoodCategory = 'grain' | 'veggie' | 'fruit' | 'protein' | 'dairy';
export type DietPreference = 'vegetarian' | 'eggetarian' | 'non-vegetarian';

export interface SolidFood {
  id: string;
  name: string;
  emoji: string;
  category: FoodCategory;
  introWeek: number;         // week number after solids start (1-indexed)
  dietFlags: DietFlag[];     // which diets this food is safe for
  whyGood: string;
  howToServe: string;
  allergen?: boolean;
}

export const SOLID_FOODS: SolidFood[] = [
  // Week 1
  {
    id: 'rice-porridge',
    name: 'Rice Porridge',
    emoji: '🍚',
    category: 'grain',
    introWeek: 1,
    dietFlags: ['veg', 'jain'],
    whyGood: 'Easy to digest and rarely causes allergies — the perfect first food.',
    howToServe: 'Cook rice in extra water until very soft, blend to a smooth runny paste.',
  },
  {
    id: 'banana',
    name: 'Banana',
    emoji: '🍌',
    category: 'fruit',
    introWeek: 1,
    dietFlags: ['veg', 'jain'],
    whyGood: 'Soft, naturally sweet and packed with potassium and quick energy.',
    howToServe: 'Mash a ripe banana with a fork until completely smooth.',
  },
  {
    id: 'sweet-potato',
    name: 'Sweet Potato',
    emoji: '🍠',
    category: 'veggie',
    introWeek: 1,
    dietFlags: ['veg', 'jain'],
    whyGood: 'Rich in beta-carotene and Vitamin A, great for eyesight and immunity.',
    howToServe: 'Steam or boil until very soft, then blend with a little water or breastmilk.',
  },

  // Week 2
  {
    id: 'moong-dal-water',
    name: 'Moong Dal Water',
    emoji: '🫘',
    category: 'protein',
    introWeek: 2,
    dietFlags: ['veg', 'jain'],
    whyGood: 'Yellow moong is the easiest lentil for tiny tummies and adds gentle protein.',
    howToServe: 'Boil yellow moong dal until very soft, strain the water, serve warm.',
  },
  {
    id: 'apple-puree',
    name: 'Apple Puree',
    emoji: '🍎',
    category: 'fruit',
    introWeek: 2,
    dietFlags: ['veg', 'jain'],
    whyGood: 'Fibre-rich and mildly sweet — helps with healthy digestion.',
    howToServe: 'Peel, core and steam apple pieces, then blend to a smooth puree.',
  },

  // Week 3
  {
    id: 'carrot-puree',
    name: 'Carrot Puree',
    emoji: '🥕',
    category: 'veggie',
    introWeek: 3,
    dietFlags: ['veg'],  // not jain — root vegetable
    whyGood: 'High in beta-carotene and Vitamin A, naturally sweet babies usually love it.',
    howToServe: 'Peel, steam until soft, blend with a little water to smooth consistency.',
  },
  {
    id: 'pear-puree',
    name: 'Pear Puree',
    emoji: '🍐',
    category: 'fruit',
    introWeek: 3,
    dietFlags: ['veg', 'jain'],
    whyGood: 'Very gentle on digestion and a good source of Vitamin C.',
    howToServe: 'Peel, core and steam pear until soft, blend until smooth.',
  },

  // Week 4
  {
    id: 'ragi-porridge',
    name: 'Ragi Porridge',
    emoji: '🌾',
    category: 'grain',
    introWeek: 4,
    dietFlags: ['veg', 'jain'],
    whyGood: 'Finger millet is a calcium powerhouse — great for bone development.',
    howToServe: 'Mix ragi flour with water, cook on low heat stirring continuously until thick, thin with milk.',
  },
  {
    id: 'pumpkin-puree',
    name: 'Pumpkin Puree',
    emoji: '🎃',
    category: 'veggie',
    introWeek: 4,
    dietFlags: ['veg', 'jain'],
    whyGood: 'High in Vitamin A and fibre, and its mild sweetness makes it very baby-friendly.',
    howToServe: 'Peel, deseed and steam pumpkin until soft, blend to a smooth puree.',
  },

  // Week 5
  {
    id: 'oats-porridge',
    name: 'Oats Porridge',
    emoji: '🥣',
    category: 'grain',
    introWeek: 5,
    dietFlags: ['veg', 'jain'],
    whyGood: 'Iron-fortified oats provide fibre and energy to support rapid growth.',
    howToServe: 'Cook baby oats in water or milk until smooth and creamy.',
  },
  {
    id: 'papaya',
    name: 'Papaya',
    emoji: '🍈',
    category: 'fruit',
    introWeek: 5,
    dietFlags: ['veg', 'jain'],
    whyGood: 'Contains papain enzyme that aids digestion and is rich in Vitamin C.',
    howToServe: 'Peel and deseed ripe papaya, mash or blend until smooth.',
  },

  // Week 6
  {
    id: 'spinach-dal',
    name: 'Spinach Dal',
    emoji: '🍃',
    category: 'protein',
    introWeek: 6,
    dietFlags: ['veg', 'jain'],
    whyGood: 'Iron from both dal and spinach — important as breast milk iron starts reducing.',
    howToServe: 'Cook moong dal and blanched spinach together, blend to smooth puree.',
  },
  {
    id: 'mango-puree',
    name: 'Mango Puree',
    emoji: '🥭',
    category: 'fruit',
    introWeek: 6,
    dietFlags: ['veg', 'jain'],
    whyGood: 'Packed with Vitamin C, A and natural sugars — most babies absolutely love it.',
    howToServe: 'Blend ripe mango pulp until smooth. Only in season.',
  },

  // Week 7
  {
    id: 'potato-mash',
    name: 'Potato Mash',
    emoji: '🥔',
    category: 'veggie',
    introWeek: 7,
    dietFlags: ['veg'],  // not jain — root vegetable
    whyGood: 'Easy to digest, filling and a good source of potassium and Vitamin B6.',
    howToServe: 'Boil potato until very soft, mash with a little breast milk or water. No salt.',
  },
  {
    id: 'lentil-soup',
    name: 'Lentil Soup (Dal)',
    emoji: '🫕',
    category: 'protein',
    introWeek: 7,
    dietFlags: ['veg', 'jain'],
    whyGood: 'Lentils are an excellent plant-based protein and iron source for growing babies.',
    howToServe: 'Cook masoor or toor dal until mushy, thin to soup consistency with water.',
  },

  // Week 8
  {
    id: 'paneer',
    name: 'Paneer',
    emoji: '🧀',
    category: 'dairy',
    introWeek: 8,
    dietFlags: ['veg', 'jain'],
    whyGood: 'High in protein and calcium, and a great first dairy food for Indian babies.',
    howToServe: 'Blend soft fresh paneer with a little water until smooth.',
    allergen: true,
  },
  {
    id: 'beetroot-puree',
    name: 'Beetroot Puree',
    emoji: '🫛',
    category: 'veggie',
    introWeek: 8,
    dietFlags: ['veg'],  // not jain — root vegetable
    whyGood: 'Rich in folate and iron, and that bright red colour is a fun introduction to colours.',
    howToServe: 'Steam or boil beetroot until very tender, blend with a little water.',
  },

  // Week 9
  {
    id: 'yogurt',
    name: 'Plain Yogurt (Curd)',
    emoji: '🥛',
    category: 'dairy',
    introWeek: 9,
    dietFlags: ['veg', 'jain'],
    whyGood: 'Probiotics in curd support gut health and the calcium builds strong bones.',
    howToServe: 'Offer plain, full-fat homemade curd at room temperature. Never add sugar.',
    allergen: true,
  },
  {
    id: 'broccoli',
    name: 'Broccoli',
    emoji: '🥦',
    category: 'veggie',
    introWeek: 9,
    dietFlags: ['veg', 'jain'],
    whyGood: 'Iron, Vitamin C, and folate — one of the most nutrient-dense first vegetables.',
    howToServe: 'Steam florets until very soft, blend or mash well.',
  },

  // Week 10
  {
    id: 'egg-yolk',
    name: 'Egg Yolk',
    emoji: '🥚',
    category: 'protein',
    introWeek: 10,
    dietFlags: ['egg'],  // eggetarian and non-veg only
    whyGood: 'Rich in choline which is critical for brain development and iron for growth.',
    howToServe: 'Hard-boil an egg, remove white, mash yolk with a little breast milk.',
    allergen: true,
  },
  {
    id: 'avocado',
    name: 'Avocado',
    emoji: '🥑',
    category: 'fruit',
    introWeek: 10,
    dietFlags: ['veg', 'jain'],
    whyGood: 'Healthy fats similar to breast milk fat support brain and nervous system development.',
    howToServe: 'Mash ripe avocado with a fork until completely smooth.',
  },

  // Week 11
  {
    id: 'watermelon',
    name: 'Watermelon',
    emoji: '🍉',
    category: 'fruit',
    introWeek: 11,
    dietFlags: ['veg', 'jain'],
    whyGood: 'Mostly water, so great for hydration, plus lycopene for immunity.',
    howToServe: 'Remove seeds, blend seedless flesh and strain to remove fibres.',
  },
  {
    id: 'green-peas',
    name: 'Green Peas',
    emoji: '🫛',
    category: 'veggie',
    introWeek: 11,
    dietFlags: ['veg', 'jain'],
    whyGood: 'Protein, Vitamin K and fibre in one small package.',
    howToServe: 'Steam fresh or frozen peas, blend until smooth and strain.',
  },

  // Week 12
  {
    id: 'idli',
    name: 'Soft Idli',
    emoji: '🫓',
    category: 'grain',
    introWeek: 12,
    dietFlags: ['veg', 'jain'],
    whyGood: 'Fermented rice and lentils create probiotics and improve nutrient absorption.',
    howToServe: 'Soak soft idli in dal or rasam until mushy.',
  },
  {
    id: 'khichdi',
    name: 'Dal Khichdi',
    emoji: '🍲',
    category: 'grain',
    introWeek: 12,
    dietFlags: ['veg', 'jain'],
    whyGood: 'A complete protein — rice and lentils together provide all essential amino acids.',
    howToServe: 'Cook rice and moong dal together until very soft and mushy. No salt or spices.',
  },

  // Week 14
  {
    id: 'cauliflower',
    name: 'Cauliflower',
    emoji: '🥬',
    category: 'veggie',
    introWeek: 14,
    dietFlags: ['veg', 'jain'],
    whyGood: 'Rich in Vitamin C and folate, and a gentle introduction to cruciferous vegetables.',
    howToServe: 'Steam florets until very soft, blend with a little water.',
  },
  {
    id: 'peach',
    name: 'Peach',
    emoji: '🍑',
    category: 'fruit',
    introWeek: 14,
    dietFlags: ['veg', 'jain'],
    whyGood: 'Vitamin A, C and antioxidants in a naturally sweet package.',
    howToServe: 'Peel, remove pit, steam if not fully ripe, blend smooth.',
  },

  // Week 16
  {
    id: 'tofu',
    name: 'Tofu',
    emoji: '🥗',
    category: 'protein',
    introWeek: 16,
    dietFlags: ['veg', 'jain'],
    whyGood: 'Plant-based complete protein with calcium — great for vegetarian babies.',
    howToServe: 'Use soft silken tofu, blend or mash smoothly.',
    allergen: true,
  },
  {
    id: 'corn',
    name: 'Corn',
    emoji: '🌽',
    category: 'grain',
    introWeek: 16,
    dietFlags: ['veg', 'jain'],
    whyGood: 'A source of B vitamins and fibre, introduced later as harder to digest.',
    howToServe: 'Cook corn until very soft, blend and strain to remove skins.',
  },

  // Week 20 — expanding textures
  {
    id: 'chicken',
    name: 'Chicken (minced)',
    emoji: '🍗',
    category: 'protein',
    introWeek: 20,
    dietFlags: ['nonveg'],
    whyGood: 'Excellent source of haem iron and complete protein — best absorbed iron source.',
    howToServe: 'Cook minced chicken thoroughly, blend with water to smooth paste.',
  },
  {
    id: 'fish',
    name: 'Fish (white)',
    emoji: '🐟',
    category: 'protein',
    introWeek: 52,  // after 12 months
    dietFlags: ['nonveg'],
    whyGood: 'Omega-3 DHA in fish is the single best nutrient for brain development.',
    howToServe: 'Steam white fish (rohu, sole, tilapia), remove all bones carefully, flake finely.',
    allergen: true,
  },
];

// ─── Helper functions ─────────────────────────────────────────────────────────

/**
 * Returns foods for a given week number, filtered by diet preference and baby age in months.
 */
export function getFoodsForWeek(
  week: number,
  dietPreference: DietPreference | undefined,
  babyAgeMonths: number
): SolidFood[] {
  return SOLID_FOODS.filter((food) => {
    if (food.introWeek !== week) return false;

    // Non-veg foods only after 12 months
    if (food.dietFlags.includes('nonveg') && babyAgeMonths < 12) return false;

    // Filter by diet preference
    if (!dietPreference || dietPreference === 'vegetarian') {
      return food.dietFlags.includes('veg');
    }
    if (dietPreference === 'eggetarian') {
      return food.dietFlags.includes('veg') || food.dietFlags.includes('egg');
    }
    // non-vegetarian: all foods
    return true;
  });
}

/**
 * Returns all weeks that have foods, up to the baby's current week of solids.
 */
export function getAvailableWeeks(
  solidsStartDate: string,
  dietPreference: DietPreference | undefined,
  babyAgeMonths: number
): number[] {
  const start = new Date(solidsStartDate);
  const now = new Date();
  const weeksSinceStart = Math.floor((now.getTime() - start.getTime()) / (7 * 24 * 60 * 60 * 1000)) + 1;

  const allWeeks = [...new Set(SOLID_FOODS.map((f) => f.introWeek))].sort((a, b) => a - b);
  return allWeeks.filter((w) => {
    if (w > weeksSinceStart) return false;
    return getFoodsForWeek(w, dietPreference, babyAgeMonths).length > 0;
  });
}

/**
 * Returns the current week number of solids (1-indexed).
 */
export function getCurrentSolidsWeek(solidsStartDate: string): number {
  const start = new Date(solidsStartDate);
  const now = new Date();
  return Math.max(1, Math.floor((now.getTime() - start.getTime()) / (7 * 24 * 60 * 60 * 1000)) + 1);
}

/**
 * Returns baby age in completed full months.
 * A baby born Nov 25 is 4 months old on Apr 9 (not 5, which completes on Apr 25).
 * Handles Date, Firestore Timestamp, or ISO string.
 */
export function ageInMonths(dob: any): number {
  let d: Date;
  if (dob && typeof dob.toDate === 'function') {
    d = dob.toDate(); // Firestore Timestamp
  } else if (dob instanceof Date) {
    d = dob;
  } else {
    d = new Date(dob);
  }
  const now = new Date();
  let months = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
  // Subtract 1 if the birth day hasn't been reached yet this month
  if (now.getDate() < d.getDate()) {
    months -= 1;
  }
  return months;
}
